import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  signal,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { ProjectorsFacade } from '@galabid/gb-dashboard/projector/data-access'
import { UntilDestroy } from '@ngneat/until-destroy'
import confetti from 'canvas-confetti'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { PresenterWidgetsFacade } from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.facade'
import {
  ProjectorNotificationData,
  WidgetModel,
  WidgetType,
} from 'libs/gb-dashboard/presenter/data-access-presenter-widgets/src/lib/+state/presenter-widgets.models'
import { PresenterElementTypeEnum } from 'libs/gb-dashboard/projector/data-access/src'
import { ProjectorContainersFacade } from 'libs/gb-dashboard/presenter/data-access-projector-containers/src/lib/+state/projector-containers.facade'
import { ProjectorTemplateRawConfig } from 'libs/gb-dashboard/presenter/data-access-projector-templates/src/lib/+state/projector-templates.models'
import { GbPresenterColorResolverService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-color-resolver.service'
import { PresenterContextEnum } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-context/presenter-context.enum'
import { PresenterContextService } from 'libs/gb-dashboard/presenter/utils/src/lib/gb-presenter-context/presenter-context.service'
import { combineLatest, map, Observable } from 'rxjs'
import { PRESENTER_CONTEXT } from '../../../../utils/src/lib/gb-presenter-context/presenter-context.token'
import { GbPresenterWidgetCountdownComponent } from '../gb-presenter-widget-countdown/gb-presenter-widget-countdown.component'
import { GbPresenterWidgetHonourRollComponent } from '../gb-presenter-widget-honour-roll/gb-presenter-widget-honour-roll.component'
import { GbPresenterWidgetImageComponent } from '../gb-presenter-widget-image/gb-presenter-widget-image.component'
import { GbPresenterWidgetItemTableComponent } from '../gb-presenter-widget-item-table/gb-presenter-widget-item-table.component'
import { GbPresenterWidgetNotificationComponent } from '../gb-presenter-widget-notification/gb-presenter-widget-notification.component'
import { GbPresenterWidgetQrCodeComponent } from '../gb-presenter-widget-qr-code/gb-presenter-widget-qr-code.component'
import { GbPresenterWidgetRaffleDrawComponent } from '../gb-presenter-widget-raffle-draw/gb-presenter-widget-raffle-draw.component'
import { GbPresenterWidgetRaffleWinnersListComponent } from '../gb-presenter-widget-raffle-winners-list/gb-presenter-widget-raffle-winners-list.component'
import { GbPresenterWidgetSmartTextComponent } from '../gb-presenter-widget-smart-text/gb-presenter-widget-smart-text.component'
import { GbPresenterWidgetTeamScoringComponent } from '../gb-presenter-widget-team-scoring/gb-presenter-widget-team-scoring.component'
import { GbPresenterWidgetPowerPledgeComponent } from '../gb-presenter-widget-power-pledge/gb-presenter-widget-power-pledge.component'
import { GbPresenterWidgetTextComponent } from '../gb-presenter-widget-text/gb-presenter-widget-text.component'
import { GbPresenterWidgetThermometerComponent } from '../gb-presenter-widget-thermometer/gb-presenter-widget-thermometer.component'
import { GbPresenterWidgetTotalRaisedComponent } from '../gb-presenter-widget-total-raised/gb-presenter-widget-total-raised.component'
import { GbPresenterWidgetVideoComponent } from '../gb-presenter-widget-video/gb-presenter-widget-video.component'
import { GbPresenterWidgetWrapperComponent } from '../gb-presenter-widget-wrapper/gb-presenter-widget-wrapper.component'

@UntilDestroy()
@Component({
  selector: 'gb-presenter-canvas',
  imports: [
    CommonModule,
    GbPresenterWidgetWrapperComponent,
    GbPresenterWidgetTextComponent,
    GbPresenterWidgetSmartTextComponent,
    GbPresenterWidgetItemTableComponent,
    GbPresenterWidgetImageComponent,
    GbPresenterWidgetTotalRaisedComponent,
    GbPresenterWidgetCountdownComponent,
    GbPresenterWidgetHonourRollComponent,
    GbPresenterWidgetRaffleDrawComponent,
    GbPresenterWidgetRaffleWinnersListComponent,
    GbPresenterWidgetQrCodeComponent,
    GbPresenterWidgetTeamScoringComponent,
    GbPresenterWidgetPowerPledgeComponent,
    GbPresenterWidgetThermometerComponent,
    GbPresenterWidgetNotificationComponent,
    GbPresenterWidgetVideoComponent,
  ],
  templateUrl: './gb-presenter-canvas.component.html',
  styleUrl: './gb-presenter-canvas.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GbPresenterCanvasComponent implements OnChanges, AfterViewInit, OnDestroy {
  private readonly presenterContext = inject(PRESENTER_CONTEXT)
  private readonly presenterContextService = inject(PresenterContextService)

  private forcedTemplateContextMode = false
  private previousContextMode: PresenterContextEnum | null = null

  @Input() templateList = false

  get isTemplateMode() {
    if (this.templateList) {
      return true
    }

    return this.presenterContext.mode === 'template'
  }

  @Input() previewSlideUuid: string | undefined = undefined

  @Input() templateConfig: string | ProjectorTemplateRawConfig | null | undefined = null

  @Input() templateBackgroundImageUrl: string | null | undefined = null

  @Output() openRaffleItemWarning = new EventEmitter<WidgetModel>()

  private readonly templateRawConfigSignal = signal<ProjectorTemplateRawConfig | null>(null)
  private readonly templateRawConfig$ = toObservable(this.templateRawConfigSignal)

  private readonly selectedProjectorUuid$ = combineLatest([
    this.projectorsFacade.selectedProjectorId$,
    this.templateRawConfig$,
  ]).pipe(
    map(([selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return 'template'
      }
      return this.isTemplateMode ? 'template' : this.previewSlideUuid || selectedProjectorUuid
    }),
  )

  @ViewChild('canvasOuterContainer', { static: true }) canvasOuterContainer!: ElementRef<HTMLElement>

  @ViewChild('confettiCanvas') confettiCanvas?: ElementRef<HTMLCanvasElement>

  @ViewChildren(GbPresenterWidgetRaffleDrawComponent)
  private raffleDrawWidgetComponents!: QueryList<GbPresenterWidgetRaffleDrawComponent>

  @ViewChildren(GbPresenterWidgetRaffleWinnersListComponent)
  private raffleWinnersListComponent!: QueryList<GbPresenterWidgetRaffleWinnersListComponent>

  @ViewChildren(GbPresenterWidgetNotificationComponent)
  private gbPresenterWidgetNotificationComponents!: QueryList<GbPresenterWidgetNotificationComponent>

  @Input() backgroundColor: string | null | undefined = '#fff'
  @Input() backgroundImage: string | null | undefined = null
  @Input() backgroundImageCover: boolean | null | undefined = true
  @Input() customCss: string | null | undefined = null
  @Input() zoomLevel = signal<number>(1)

  @Input() draggable = true
  @Input() responsive = false

  @Input() selectedWidget: WidgetModel | null | undefined = null
  @Input() savingWidget: Partial<WidgetModel> | null | undefined = null
  @Input() selectedWidgetOverrides: Partial<WidgetModel> | null | undefined = null

  @Input() testData = false

  @Output() deselectWidget = new EventEmitter<void>()
  @Output() widgetSelected = new EventEmitter<{ widget: WidgetModel; widgetType: WidgetType }>()
  @Output() widgetPositionChanged = new EventEmitter<Partial<WidgetModel>>()
  @Output() textWidgetCommitted = new EventEmitter<{ uuid: string; text: string }>()
  @Output() textWidgetEditFinished = new EventEmitter<{ uuid: string }>()
  @Output() elementDropped = new EventEmitter<{
    type: PresenterElementTypeEnum
    x: number
    y: number
  }>()

  @Input() inPreviewMode = false

  private customCssStyleEl: HTMLStyleElement | null = null

  onTextWidgetCommitted(widgetUuid: string, text: string): void {
    this.textWidgetCommitted.emit({ uuid: widgetUuid, text })
  }

  confettiInstance: confetti.CreateTypes | undefined = undefined

  private parseTemplateRawConfig(
    templateConfig: string | ProjectorTemplateRawConfig | null | undefined,
  ): ProjectorTemplateRawConfig | null {
    if (!templateConfig) {
      return null
    }

    if (typeof templateConfig !== 'string') {
      return templateConfig
    }

    try {
      return JSON.parse(templateConfig) as ProjectorTemplateRawConfig
    } catch {
      return null
    }
  }

  private normalizeWidgets<T extends { projector?: string }>(
    widgets: T[] | undefined,
  ): Array<T & { projector: string }> {
    return (widgets ?? []).map((w) => ({ ...w, projector: 'template' }))
  }

  onTextWidgetEditFinished(widgetUuid: string): void {
    this.textWidgetEditFinished.emit({ uuid: widgetUuid })
  }

  aspectRatio$: Observable<string> = combineLatest([
    this.projectorContainersFacade.selectedProjectorContainer$,
    this.templateRawConfig$,
  ]).pipe(
    map(([projectorContainer, templateRawConfig]) =>
      templateRawConfig ? '16:9' : projectorContainer?.aspect_ratio || '16:9',
    ),
  )

  designDimensions$ = this.aspectRatio$.pipe(map((aspectRatio) => this.calculateDesignDimensions(aspectRatio)))

  aspectRatioParts$ = this.aspectRatio$.pipe(
    map((ratio) => {
      const [w, h] = ratio.split(':').map(Number)
      return { w: w || 16, h: h || 9 }
    }),
  )

  imageWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allImageWidgets$,
    this.selectedProjectorUuid$,
    this.templateRawConfig$,
  ]).pipe(
    map(([widgets, selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return this.normalizeWidgets(templateRawConfig.ImageProjectorWidget)
      }
      return widgets.filter((widget) => widget.projector === selectedProjectorUuid)
    }),
  )

  thermometerWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allThermometerWidgets$,
    this.selectedProjectorUuid$,
    this.templateRawConfig$,
  ]).pipe(
    map(([allThermometerWidgets, selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return this.normalizeWidgets(templateRawConfig.ThermometerProjectorWidget)
      }
      return allThermometerWidgets.filter((thermometerWidget) => thermometerWidget.projector === selectedProjectorUuid)
    }),
  )

  notificationWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allNotificationWidgets$,
    this.selectedProjectorUuid$,
    this.templateRawConfig$,
  ]).pipe(
    map(([widgets, selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return this.normalizeWidgets(templateRawConfig.NotificationProjectorWidget)
      }
      return widgets.filter((widget) => widget.projector === selectedProjectorUuid)
    }),
  )

  textWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allTextWidgets$,
    this.selectedProjectorUuid$,
    this.templateRawConfig$,
  ]).pipe(
    map(([allTextWidgets, selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return this.normalizeWidgets(templateRawConfig.TextProjectorWidget)
      }
      return allTextWidgets.filter((textWidget) => textWidget.projector === selectedProjectorUuid)
    }),
  )

  smartTextWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allSmartTextWidgets$,
    this.selectedProjectorUuid$,
    this.templateRawConfig$,
  ]).pipe(
    map(([allSmartTextWidgets, selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return this.normalizeWidgets(templateRawConfig.SmartTextProjectorWidget)
      }
      return allSmartTextWidgets.filter((smartTextWidget) => smartTextWidget.projector === selectedProjectorUuid)
    }),
  )

  itemTableWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allItemTableWidgets$,
    this.selectedProjectorUuid$,
    this.templateRawConfig$,
  ]).pipe(
    map(([allItemTableWidgets, selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return this.normalizeWidgets(templateRawConfig.ItemTableProjectorWidget)
      }
      return allItemTableWidgets.filter((itemTableWidget) => itemTableWidget.projector === selectedProjectorUuid)
    }),
  )

  totalRaisedWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allTotalRaisedWidgets$,
    this.selectedProjectorUuid$,
    this.templateRawConfig$,
  ]).pipe(
    map(([allTotalRaisedWidgets, selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return this.normalizeWidgets(templateRawConfig.TotalRaisedProjectorWidget)
      }
      return allTotalRaisedWidgets.filter((totalRaisedWidget) => totalRaisedWidget.projector === selectedProjectorUuid)
    }),
  )

  countdownWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allCountdownWidgets$,
    this.selectedProjectorUuid$,
    this.templateRawConfig$,
  ]).pipe(
    map(([allCountdownWidgets, selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return this.normalizeWidgets(templateRawConfig.CountDownProjectorWidget)
      }
      return allCountdownWidgets.filter((countdownWidget) => countdownWidget.projector === selectedProjectorUuid)
    }),
  )

  honourRollWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allHonourRollWidgets$,
    this.selectedProjectorUuid$,
    this.templateRawConfig$,
  ]).pipe(
    map(([allHonourRollWidgets, selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return this.normalizeWidgets(templateRawConfig.HonourRollProjectorWidget)
      }
      return allHonourRollWidgets.filter((honourRollWidget) => honourRollWidget.projector === selectedProjectorUuid)
    }),
  )

  raffleDrawWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allRaffleDrawWidgets$,
    this.selectedProjectorUuid$,
    this.templateRawConfig$,
  ]).pipe(
    map(([allRaffleDrawWidgets, selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return this.normalizeWidgets(templateRawConfig.RaffleDrawProjectorWidget)
      }
      return allRaffleDrawWidgets.filter((raffleDrawWidget) => raffleDrawWidget.projector === selectedProjectorUuid)
    }),
  )

  raffleWinnersWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allRaffleWinnersWidgets$,
    this.selectedProjectorUuid$,
    this.templateRawConfig$,
  ]).pipe(
    map(([allRaffleWinnersWidgets, selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return this.normalizeWidgets(templateRawConfig.RaffleWinnersProjectorWidget)
      }
      return allRaffleWinnersWidgets.filter(
        (raffleWinnersWidget) => raffleWinnersWidget.projector === selectedProjectorUuid,
      )
    }),
  )

  qrCodeWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allQrCodeWidgets$,
    this.selectedProjectorUuid$,
    this.templateRawConfig$,
  ]).pipe(
    map(([allQrCodeWidgets, selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return this.normalizeWidgets(templateRawConfig.QrCodeProjectorWidget)
      }
      return allQrCodeWidgets.filter((qrCodeWidget) => qrCodeWidget.projector === selectedProjectorUuid)
    }),
  )

  teamScoringWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allTeamScoringWidgets$,
    this.selectedProjectorUuid$,
    this.templateRawConfig$,
  ]).pipe(
    map(([allTeamScoringWidgets, selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return this.normalizeWidgets(templateRawConfig.TeamScoringProjectorWidget)
      }
      return allTeamScoringWidgets.filter((teamScoringWidget) => teamScoringWidget.projector === selectedProjectorUuid)
    }),
  )

  powerPledgeWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allPowerPledgeWidgets$,
    this.selectedProjectorUuid$,
    this.templateRawConfig$,
  ]).pipe(
    map(([widgets, selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return this.normalizeWidgets(templateRawConfig.PowerPledgeProjectorWidget)
      }
      return widgets.filter((widget) => widget.projector === selectedProjectorUuid)
    }),
  )

  videoWidgets$ = combineLatest([
    this.presenterWidgetsFacade.allVideoWidgets$,
    this.selectedProjectorUuid$,
    this.templateRawConfig$,
  ]).pipe(
    map(([allVideoWidgets, selectedProjectorUuid, templateRawConfig]) => {
      if (templateRawConfig) {
        return []
      }
      return allVideoWidgets.filter((videoWidget) => videoWidget.projector === selectedProjectorUuid)
    }),
  )

  WidgetType = WidgetType

  auction$ = this.projectorContainersFacade.selectedProjectorContainer$.pipe(map((container) => container?.auction))

  selectedAuction$ = this.selectedAuctionFacade.auction$

  constructor(
    private projectorContainersFacade: ProjectorContainersFacade,
    private presenterWidgetsFacade: PresenterWidgetsFacade,
    private projectorsFacade: ProjectorsFacade,
    private selectedAuctionFacade: SelectedAuctionFacade,
    private colorResolver: GbPresenterColorResolverService,
  ) {}

  getResolvedColor(color: string | null | undefined): string | null {
    return this.colorResolver.resolve(color) || null
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['templateList']) {
      this.syncTemplateContextMode()
    }

    if (changes['templateConfig'] || changes['templateBackgroundImageUrl']) {
      const rawConfig = this.parseTemplateRawConfig(this.templateConfig)
      this.templateRawConfigSignal.set(rawConfig)

      if (rawConfig) {
        this.backgroundColor = rawConfig.Projector.background_color ?? ''
        this.backgroundImageCover = rawConfig.Projector.background_image_cover ?? true
        this.customCss = rawConfig.Projector.custom_css ?? ''
        this.backgroundImage = this.templateBackgroundImageUrl ?? null

        this.applyCustomCss()
      }
    }

    if (changes['customCss']) {
      this.applyCustomCss()
    }
  }

  ngAfterViewInit(): void {
    const canvas = this.confettiCanvas?.nativeElement

    this.applyCustomCss()

    if (canvas) {
      this.confettiInstance = confetti.create(canvas, {
        resize: true,
        useWorker: false,
      })
    }
  }

  ngOnDestroy(): void {
    if (this.forcedTemplateContextMode) {
      this.presenterContextService.setMode(this.previousContextMode ?? PresenterContextEnum.AUCTION)
    }

    this.customCssStyleEl?.remove()
    this.customCssStyleEl = null

    if (this.confettiInstance) {
      this.confettiInstance.reset()
    }
  }

  private syncTemplateContextMode(): void {
    if (this.templateList) {
      if (!this.presenterContextService.isTemplateMode) {
        this.previousContextMode = this.presenterContextService.mode as PresenterContextEnum
        this.presenterContextService.setMode(PresenterContextEnum.TEMPLATE)
        this.forcedTemplateContextMode = true
      }
      return
    }

    if (this.forcedTemplateContextMode) {
      this.presenterContextService.setMode(this.previousContextMode ?? PresenterContextEnum.AUCTION)
      this.forcedTemplateContextMode = false
      this.previousContextMode = null
    }
  }

  private applyCustomCss(): void {
    const host = this.canvasOuterContainer?.nativeElement
    if (!host) {
      return
    }

    if (!this.customCssStyleEl) {
      this.customCssStyleEl = document.createElement('style')
      this.customCssStyleEl.setAttribute('data-gb-presenter-custom-css', 'true')
      host.appendChild(this.customCssStyleEl)
    }

    const cssText = (this.customCss || '').trim()
    this.customCssStyleEl.textContent = cssText
  }

  fireConfetti(): void {
    const fire = this.confettiInstance || confetti

    fire({
      particleCount: 150,
      spread: 100,
      origin: { y: 0.6 },
    })
  }

  private calculateDesignDimensions(aspectRatio: string): { width: number; height: number } {
    const baseWidth = 800

    const [widthRatio, heightRatio] = aspectRatio.split(':').map(Number)

    if (!widthRatio || !heightRatio) {
      return { width: baseWidth, height: Math.round(baseWidth * (9 / 16)) }
    }

    const height = Math.round(baseWidth * (heightRatio / widthRatio))

    return { width: baseWidth, height }
  }

  clearSelectedWidget() {
    if (!this.inPreviewMode) {
      this.deselectWidget.emit()
    }
  }

  triggerRaffleDrawPreview(selectedWidgetUuid: string | null | undefined): void {
    if (!selectedWidgetUuid) {
      return
    }

    const drawComponents = this.raffleDrawWidgetComponents?.toArray() ?? []
    const winnersComponents = this.raffleWinnersListComponent?.toArray() ?? []

    if (this.isTemplateMode) {
      drawComponents.forEach((component) => component.previewDraw())
      winnersComponents.forEach((component) => component.previewDraw())
      return
    }

    const selectedDrawComponent = drawComponents.find((c) => c.raffleDrawWidget?.uuid === selectedWidgetUuid)
    const selectedWinnersComponent = winnersComponents.find((c) => {
      const widget = c.raffleWinnersWidget()
      return widget.uuid === selectedWidgetUuid
    })

    const selectedWinnersWidget = selectedWinnersComponent?.raffleWinnersWidget()
    const raffleItemUuid = selectedDrawComponent?.raffleDrawWidget?.raffle_item ?? selectedWinnersWidget?.raffle_item

    if (!raffleItemUuid) {
      selectedDrawComponent?.previewDraw()
      selectedWinnersComponent?.previewDraw()
      return
    }

    const drawComponent = drawComponents.find((c) => c.raffleDrawWidget?.raffle_item === raffleItemUuid)
    const winnersListComponent = winnersComponents.find((c) => c.raffleWinnersWidget().raffle_item === raffleItemUuid)

    drawComponent?.previewDraw()
    winnersListComponent?.previewDraw()
  }

  resetRaffleDrawPreview(selectedWidgetUuid: string | null | undefined): void {
    if (!selectedWidgetUuid) {
      return
    }

    const drawComponents = this.raffleDrawWidgetComponents?.toArray() ?? []
    const winnersComponents = this.raffleWinnersListComponent?.toArray() ?? []

    if (this.isTemplateMode) {
      winnersComponents.forEach((component) => component.resetPreview())
      drawComponents.forEach((component) => component.resetPreview())
      return
    }

    const selectedDrawComponent = drawComponents.find((c) => c.raffleDrawWidget?.uuid === selectedWidgetUuid)
    const selectedWinnersComponent = winnersComponents.find((c) => {
      const widget = c.raffleWinnersWidget()
      return widget.uuid === selectedWidgetUuid
    })

    const selectedWinnersWidget = selectedWinnersComponent?.raffleWinnersWidget()
    const raffleItemUuid = selectedDrawComponent?.raffleDrawWidget?.raffle_item ?? selectedWinnersWidget?.raffle_item

    if (!raffleItemUuid) {
      selectedWinnersComponent?.resetPreview()
      return
    }

    const winnersListComponent = winnersComponents.find((c) => c.raffleWinnersWidget().raffle_item === raffleItemUuid)
    winnersListComponent?.resetPreview()

    const drawComponent = drawComponents.find((c) => c.raffleDrawWidget?.raffle_item === raffleItemUuid)
    drawComponent?.resetPreview()
  }

  triggerRaffleDrawFromRealtime(itemUuid: string | null | undefined, ticketNumber: number, _prize: number): void {
    if (!itemUuid) {
      return
    }

    const component = this.raffleDrawWidgetComponents
      ?.toArray()
      .find((c) => c.raffleDrawWidget?.raffle_item === itemUuid)

    component?.triggerDrawFromRealtime(ticketNumber)
  }

  triggerNotification(data: ProjectorNotificationData): void {
    const components = this.gbPresenterWidgetNotificationComponents

    components.map((component) => {
      component.showNotification(data)
    })
  }

  onWidgetMouseDown(widget: WidgetModel, widgetType: WidgetType, event: MouseEvent): void {
    if (!this.draggable) {
      return
    }

    event.stopPropagation()
    this.widgetSelected.emit({ widget, widgetType })
  }

  onWidgetPositionChanged(widget: Partial<WidgetModel>): void {
    this.widgetPositionChanged.emit(widget)
  }

  onDragOver(event: DragEvent): void {
    if (this.inPreviewMode) {
      return
    }

    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy'
    }
  }

  onDrop(event: DragEvent): void {
    if (this.inPreviewMode) {
      return
    }

    event.preventDefault()

    const data = event.dataTransfer?.getData('application/json')
    if (!data) {
      return
    }

    try {
      const element = JSON.parse(data)
      const type = element.id as PresenterElementTypeEnum

      const canvas = event.currentTarget as HTMLElement
      const rect = canvas.getBoundingClientRect()

      const x = ((event.clientX - rect.left) / rect.width) * 100
      const y = ((event.clientY - rect.top) / rect.height) * 100

      this.elementDropped.emit({ type, x, y })
    } catch (e) {
      console.error('Failed to parse dropped element data', e)
    }
  }

  getWidget<T extends { uuid: string }>(widget: T): T {
    if (widget.uuid === this.selectedWidget?.uuid) {
      return { ...widget, ...(this.selectedWidgetOverrides || {}) } as T
    }

    if (widget.uuid === this.savingWidget?.uuid) {
      return { ...widget, ...(this.savingWidget || {}) } as T
    }

    return widget
  }

  getOuterContainerSize(): { width: number; height: number } {
    const element = this.canvasOuterContainer?.nativeElement
    const rect = element?.getBoundingClientRect()

    return {
      width: rect?.width ?? 0,
      height: rect?.height ?? 0,
    }
  }

  getProjectorCanvasSize(): { width: number; height: number } {
    const outer = this.canvasOuterContainer?.nativeElement
    const projectorCanvas = outer?.querySelector<HTMLElement>('.projector-canvas')
    const rect = projectorCanvas?.getBoundingClientRect()

    return {
      width: rect?.width ?? 0,
      height: rect?.height ?? 0,
    }
  }
}
